import { issueExtractor } from '@app/utils'
import { useDeferredValue, useEffect, useRef, useState } from 'react'
import type { Feed } from 'a11ywatch-web-wasm-pro'
import { PageIssue } from '@app/types'
import { FilterManager } from '@app/managers/filters'

type LiveFeedProps = {
  domain: string
  pageUrl: string
  feed: Feed
  isHidden?: boolean
}

export const useLiveFeed = ({
  domain,
  pageUrl,
  feed,
  isHidden,
}: LiveFeedProps) => {
  const currentIssueCount = useRef<number>(0)

  const [sectionHidden, onToggleSection] = useState<boolean>(!!isHidden)
  const [pageIssues, setPageIssues] = useState<PageIssue[]>([])
  const [pageMatchers, setPageMatchers] = useState<Set<string>>(new Set())

  // allow feed adjustment at render for manual triggering
  const issue = useDeferredValue(feed?.get_page(domain, pageUrl))
  const pIssues = issueExtractor(issue) // array of issues extract duplex types

  useEffect(() => {
    for (const item of pageIssues) {
      const { code, selector, context } = item ?? {
        code: '',
        selector: '',
        context: '',
      }
      if (code) {
        FilterManager.addFilter(code)
        FilterManager.addGroupItem(code + selector + context, item, pageUrl)
      }
    }
  }, [pageIssues, pageUrl])

  useEffect(() => {
    const shouldUpdate = pIssues && currentIssueCount.current !== pIssues.length

    if (shouldUpdate) {
      // run side effect once per issue transition
      if (pageIssues.length < pIssues.length) {
        setPageIssues(pIssues)
      } else if (pageIssues.length > pIssues.length) {
        const matches: Set<string> = new Set()

        // determine already fixed
        for (const iss of pIssues) {
          matches.add(iss.code + iss.selector + iss.context)
        }

        setPageMatchers(matches)
      }
      currentIssueCount.current = pIssues.length
    }
  }, [pageIssues, pIssues, setPageIssues, currentIssueCount, setPageMatchers])

  return {
    pageMatchers,
    sectionHidden,
    onToggleSection,
    pIssues,
    issue,
    currentIssueCount,
    pageIssues,
  }
}
